import React from 'react'
import PropTypes from 'prop-types'
import { ToastContext } from './ToastProvider.logic.js'
import Toast from './Toast.jsx'

// How many depth shells to draw behind the front toast. Two is enough to read
// as "a pile"; the exact backlog size is spelled out in the counter above it.
const MAX_SHELLS = 2

function ToastProviderView({
  children,
  currentToast,
  queuedCount = 0,
  addToast,
  removeToast,
  // No default: the only consumer is ToastProvider, which always supplies it,
  // and React accepts an undefined onClick. A no-op default would be an
  // unreachable branch with no observable difference from undefined.
  dismissAll,
}) {
  const shellCount = Math.min(queuedCount, MAX_SHELLS)

  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className='toast-container'>
        {currentToast && (
          <div className='toast-stack' role='status' aria-live='polite'>
            {queuedCount > 0 && (
              <div className='toast-stack__queue'>
                <span data-testid='toast-queue-count'>{queuedCount} more</span>
                <button
                  type='button'
                  onClick={dismissAll}
                  data-testid='toast-dismiss-all'
                >
                  Dismiss all
                </button>
              </div>
            )}

            {/* The shells are absolutely positioned against this box, so they
                inherit the front toast's exact dimensions — no hard-coded
                heights to drift when a message wraps. */}
            <div className='toast-stack__cards'>
              {shellCount > 1 && (
                <div
                  className='toast-stack__shell toast-stack__shell--back'
                  data-testid='toast-stack-shell'
                  aria-hidden='true'
                />
              )}
              {shellCount > 0 && (
                <div
                  className='toast-stack__shell toast-stack__shell--mid'
                  data-testid='toast-stack-shell'
                  aria-hidden='true'
                />
              )}
              <Toast
                key={currentToast.id}
                message={currentToast.message}
                type={currentToast.type}
                count={currentToast.count}
                onClose={removeToast}
              />
            </div>
          </div>
        )}
      </div>
    </ToastContext.Provider>
  )
}

ToastProviderView.propTypes = {
  children: PropTypes.any,
  currentToast: PropTypes.shape({
    id: PropTypes.number,
    message: PropTypes.string,
    type: PropTypes.string,
    count: PropTypes.number,
  }),
  queuedCount: PropTypes.number,
  addToast: PropTypes.func.isRequired,
  removeToast: PropTypes.func.isRequired,
  dismissAll: PropTypes.func,
}

export default ToastProviderView
